/**
 * services/autoI18n.ts · DOM 自动翻译 v341 (P1 124 页 ZH→VI 不动源码)
 *
 * 复用 v200.16.1 TN_AUTO_I18N v154 设计:
 *   - 加载时 fetch zh.json + vi.json + zh-vi-hardcoded.json 构建反查 map
 *   - MutationObserver 监 DOM addedNodes 增量翻译
 *   - 切 VI 时全 DOM 重扫
 *   - 切 ZH 时 reload (恢复原 ZH 硬编码 · 简化反 mutation)
 *
 * 性能保护:
 *   - 跳过 INPUT/TEXTAREA/SCRIPT/STYLE/CODE/PRE
 *   - 跳过 data-tn-no-i18n / translate="no" / .notranslate
 *   - requestIdleCallback throttle
 *   - cap 翻译次数 (per page <500 次防过载)
 */

import { getCurrentLang } from '@core/i18n';

let zhToVi: Map<string, string> | null = null;
let isObserving = false;
let translateCount = 0;
const MAX_TRANSLATIONS_PER_PASS = 800;
const TRANSLATED_FLAG = '_tnI18nTranslated';

/** 构建 ZH→VI 反查 map */
function buildReverseMap(zhDict: any, viDict: any, prefix = ''): void {
  if (!zhToVi) zhToVi = new Map();
  if (typeof zhDict !== 'object' || typeof viDict !== 'object') return;
  for (const key of Object.keys(zhDict)) {
    const zhVal = zhDict[key];
    const viVal = viDict[key];
    if (typeof zhVal === 'string' && typeof viVal === 'string' && zhVal && viVal && zhVal !== viVal) {
      // ZH→VI 反查 (用 trim 后字符串作 key)
      const trimmed = zhVal.trim();
      if (trimmed.length >= 2 && trimmed.length <= 200) { // 跳过单字 + 超长
        if (!zhToVi.has(trimmed)) zhToVi.set(trimmed, viVal.trim());
      }
    } else if (typeof zhVal === 'object' && typeof viVal === 'object') {
      buildReverseMap(zhVal, viVal, prefix + key + '.');
    }
  }
}

async function loadDict(): Promise<void> {
  if (zhToVi) return; // 已加载
  zhToVi = new Map();
  try {
    const [zh, vi] = await Promise.all([
      fetch('/i18n/zh.json').then(r => r.json()).catch(() => ({})),
      fetch('/i18n/vi.json').then(r => r.json()).catch(() => ({})),
    ]);
    buildReverseMap(zh, vi);
    console.log('[autoI18n] loaded', zhToVi.size, 'ZH→VI mappings');
  } catch (err) {
    console.warn('[autoI18n] dict load failed:', err);
  }

  // 加 core 业务词典 (124 页常见硬编码 ZH)
  const CORE_DICT: Record<string, string> = {
    // 通用 button/label (复用 v200 200+ core 词典精选)
    '保存': 'Lưu', '取消': 'Hủy', '删除': 'Xóa', '编辑': 'Sửa',
    '创建': 'Tạo', '提交': 'Nộp', '搜索': 'Tìm', '筛选': 'Lọc',
    '导出': 'Xuất', '导入': 'Nhập', '刷新': 'Làm mới', '返回': 'Quay lại',
    '确认': 'Xác nhận', '是': 'Có', '否': 'Không', '加载中': 'Đang tải',
    '加载中...': 'Đang tải...', '操作': 'Thao tác', '状态': 'Trạng thái',
    '名称': 'Tên', '编号': 'Mã', '编码': 'Mã', '数量': 'Số lượng',
    '单价': 'Đơn giá', '总额': 'Tổng tiền', '总数': 'Tổng số',
    '客户': 'Khách hàng', '供应商': 'Nhà cung cấp', '产品': 'Sản phẩm',
    '物料': 'Vật tư', '原料': 'NVL', '工单': 'Lệnh SX', '订单': 'Đơn hàng',
    '报价': 'Báo giá', '发票': 'Hóa đơn', '收款': 'Thu tiền', '付款': 'Trả tiền',
    '员工': 'Nhân viên', '部门': 'Phòng ban', '工资': 'Lương', '考勤': 'Chấm công',
    '设备': 'Thiết bị', '维修': 'Bảo trì', '备件': 'Phụ tùng',
    '库存': 'Tồn kho', '入库': 'Nhập kho', '出库': 'Xuất kho',
    '盘点': 'Kiểm kho', '调拨': 'Chuyển kho', '发货': 'Giao hàng',
    '生产': 'Sản xuất', '检验': 'Kiểm tra', '合格': 'Đạt', '不合格': 'NG',
    '加急': 'Khẩn', '逾期': 'Quá hạn', '已完工': 'Hoàn thành',
    '在产': 'Đang SX', '待开工': 'Chờ SX', '已完成': 'Hoàn thành',
    '维修中': 'Đang sửa', '待审': 'Chờ duyệt', '已批': 'Đã duyệt',
    '已拒绝': 'Đã từ chối', '已发货': 'Đã giao', '已收款': 'Đã thu',
    // section
    '主页': 'Trang chủ', '财务': 'Tài chính', '销售': 'Bán hàng',
    '工程': 'Kỹ thuật', '采购': 'Mua hàng', '仓储': 'Kho',
    '品控': 'QC', '人事': 'Nhân sự', '保卫': 'Bảo vệ',
    '工人': 'Công nhân', '系统': 'Hệ thống',
    // 常见短语
    '今日': 'Hôm nay', '本月': 'Tháng này', '本周': 'Tuần này',
    '查看详情': 'Xem chi tiết', '点击查看': 'Bấm xem',
    '暂无数据': 'Chưa có dữ liệu', '暂无': 'Chưa có',
    '页面建设中': 'Trang đang xây dựng', '请选择': 'Vui lòng chọn',
    '请填写': 'Vui lòng điền', '必填': 'Bắt buộc',
    '操作成功': 'Thao tác thành công', '操作失败': 'Thao tác thất bại',
    '已提交': 'Đã nộp', '已保存': 'Đã lưu',
    // v348 · Order360 timeline 节点名 (跳过 工单/发货/已收款 已上面有)
    '销售订单': 'Đơn hàng', 'BOM 配方': 'BOM', '报工': 'Báo công',
    'QC 品检': 'QC', 'AR 开票': 'Xuất HĐ AR',
    '下一步': 'Bước tiếp', '立即处理': 'Xử lý ngay', '返回列表': 'Về DS',
    '切换订单': 'Chuyển đơn', '未配': 'Chưa cấu hình', '未派': 'Chưa giao',
    '未报工': 'Chưa BC', '未检': 'Chưa kiểm', '未入': 'Chưa nhập',
    '未发': 'Chưa giao', '未收': 'Chưa thu', '无合同': 'Không HĐ',
    '可选': 'Tùy chọn', '订单金额': 'Số tiền ĐH', '已开 AR': 'AR đã xuất',
    '未收款': 'Chưa thu',
  };
  for (const [zh, vi] of Object.entries(CORE_DICT)) {
    if (!zhToVi.has(zh)) zhToVi.set(zh, vi);
  }
  console.log('[autoI18n] total', zhToVi.size, 'ZH→VI');
}

function shouldSkipNode(node: Node): boolean {
  let n: Node | null = node;
  while (n && n !== document.body) {
    if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as HTMLElement;
      const tag = el.tagName;
      if (['SCRIPT', 'STYLE', 'CODE', 'PRE', 'INPUT', 'TEXTAREA', 'SELECT', 'OPTION'].includes(tag)) return true;
      if (el.getAttribute && el.getAttribute('translate') === 'no') return true;
      if (el.classList?.contains('notranslate')) return true;
      if (el.dataset?.tnNoI18n === '1') return true;
      if ((el as any)._tnI18nBlocked) return true;
    }
    n = n.parentNode;
  }
  return false;
}

function translateTextNode(node: Text): number {
  if (!zhToVi || zhToVi.size === 0) return 0;
  if (shouldSkipNode(node)) return 0;
  if ((node as any)[TRANSLATED_FLAG]) return 0;
  const text = node.nodeValue || '';
  if (text.length < 2 || text.length > 500) return 0;
  // 仅含 ASCII 跳过
  if (!/[一-龥]/.test(text)) return 0;

  // Strategy 1: 完整匹配
  const trimmed = text.trim();
  if (zhToVi.has(trimmed)) {
    const vi = zhToVi.get(trimmed)!;
    // 保留原 leading/trailing 空白
    const leading = text.match(/^\s*/)?.[0] || '';
    const trailing = text.match(/\s*$/)?.[0] || '';
    node.nodeValue = leading + vi + trailing;
    (node as any)[TRANSLATED_FLAG] = true;
    return 1;
  }

  // Strategy 2: 前缀:后缀 (例如 "类型: 销售订单")
  const colonMatch = text.match(/^(.+?)(\s*[:：]\s*)(.+)$/);
  if (colonMatch) {
    const [, prefix, sep, suffix] = colonMatch;
    const pTrim = prefix.trim();
    const sTrim = suffix.trim();
    if (zhToVi.has(pTrim) || zhToVi.has(sTrim)) {
      const newP = zhToVi.get(pTrim) || prefix;
      const newS = zhToVi.get(sTrim) || suffix;
      node.nodeValue = newP + sep + newS;
      (node as any)[TRANSLATED_FLAG] = true;
      return 1;
    }
  }

  return 0;
}

function walkAndTranslate(root: Node): void {
  if (!zhToVi || translateCount >= MAX_TRANSLATIONS_PER_PASS) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n: Node | null = walker.nextNode();
  while (n && translateCount < MAX_TRANSLATIONS_PER_PASS) {
    translateCount += translateTextNode(n as Text);
    n = walker.nextNode();
  }
}

let scanTimer: any = null;
function scheduleScan(root: Node = document.body): void {
  if (scanTimer) clearTimeout(scanTimer);
  scanTimer = setTimeout(() => {
    if (getCurrentLang() !== 'vi') return;
    translateCount = 0;
    walkAndTranslate(root);
  }, 100);
}

let observer: MutationObserver | null = null;
function startObserver(): void {
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    if (getCurrentLang() !== 'vi') return;
    let needScan = false;
    for (const m of mutations) {
      if (m.type === 'childList' && m.addedNodes.length > 0) {
        needScan = true;
        break;
      }
      if (m.type === 'characterData') { needScan = true; break; }
    }
    if (needScan) scheduleScan();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

function stopObserver(): void {
  if (observer) { observer.disconnect(); observer = null; }
}

export async function initAutoI18n(): Promise<void> {
  await loadDict();
  if (getCurrentLang() === 'vi') {
    scheduleScan();
    startObserver();
    isObserving = true;
  }
  // 切语言时
  document.addEventListener('tn-lang-change', (e) => {
    const lang = (e as CustomEvent).detail;
    if (lang === 'vi') {
      if (!isObserving) startObserver();
      isObserving = true;
      scheduleScan();
    } else {
      stopObserver();
      isObserving = false;
      // 切回 ZH 需 reload (因为已修改 textNode · 反推回来复杂)
      setTimeout(() => location.reload(), 500);
    }
  });
  // 路由变化后重扫
  document.addEventListener('tn-render-done', () => {
    if (getCurrentLang() === 'vi') scheduleScan();
  });
}

// 暴露 API
(window as any).TN_AUTO_I18N = {
  stats: () => ({ dictSize: zhToVi?.size || 0, isObserving, translateCount }),
  rescan: () => { translateCount = 0; scheduleScan(); },
  addMap: (zh: string, vi: string) => { zhToVi?.set(zh, vi); },
};
